import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, decodeIdToken } from '../config/api';
import { showSuccessToast, showErrorToast } from '../services/toastService';
import posthog from 'posthog-js';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  accessToken: string | null;
  refreshTokenVal: string | null;
  login: (tokens: { AccessToken: string; RefreshToken: string; idToken: string }) => void;
  logout: () => void;
  initiateLoginOtp: (phoneNumber: string, countryCode: string, showToasts?: boolean) => Promise<{ otpIdentifier: string; userIdentifier: string } | null>;
  completeLoginOtp: (phoneNumber: string, countryCode: string, otp: string, otpIdentifier: string, userIdentifier: string, showToasts?: boolean) => Promise<boolean>;
  isLoading: boolean; // Added for loading states
  updateUserData: (userData: Partial<User>) => void;
  refreshToken: () => Promise<boolean>;
}

interface User {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  groups: string[];
  profilePicture?: string;
  username?: string; // Added for vendor ID access
  // Add other user properties as needed from idToken
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshTokenVal, setRefreshTokenVal] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Start with loading true
  const navigate = useNavigate();

  const processUserLogin = (decodedUserToken: any, newAccessToken: string, newRefreshToken?: string, newIdToken?: string) => {
    const userData: User = {
      firstName: decodedUserToken['custom:firstName'] || '',
      lastName: decodedUserToken['custom:lastName'] || '',
      phoneNumber: decodedUserToken.phone_number || '',
      groups: decodedUserToken['cognito:groups'] || [],
      profilePicture: decodedUserToken['custom:profilePicUrl'] || '',
      username: decodedUserToken['cognito:username'] || decodedUserToken.sub || '',
    };
    setUser(userData);
    setAccessToken(newAccessToken);
    if (newRefreshToken) setRefreshTokenVal(newRefreshToken);
    setIsAuthenticated(true);

    // PostHog: Identify user
    if (posthog.__loaded) {
      posthog.identify(
        userData.phoneNumber, // Unique ID for the user
        {
          name: `${userData.firstName} ${userData.lastName}`,
          phone: userData.phoneNumber,
          groups: userData.groups.join(', '),
          profilePictureUrl: userData.profilePicture,
        }
      );
      posthog.capture('login_successful', { method: 'otp' });
    }
  };

  useEffect(() => {
    // Check for tokens in localStorage on initial load
    const checkAuthStatus = async () => {
      const storedAccessToken = localStorage.getItem('accessToken');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      const storedIdToken = localStorage.getItem('idToken');

      if (storedAccessToken && storedRefreshToken && storedIdToken) {
        const decodedUser = decodeIdToken(storedIdToken);
        if (decodedUser) {
          // Basic check for token expiry (can be more sophisticated)
          const isTokenExpired = decodedUser.exp * 1000 < Date.now();
          if (!isTokenExpired) {
            processUserLogin(decodedUser, storedAccessToken, storedRefreshToken, storedIdToken);
            setIsLoading(false);
          } else {
            // Attempt to refresh token if expired, and wait for it to complete
            await handleTokenRefresh(storedRefreshToken, true);
          }
        } else {
          // Invalid token, clear storage
          clearAuthDataAndPostHog();
          setIsLoading(false);
        }
      } else {
        clearAuthDataAndPostHog();
        setIsLoading(false); // Finished initial auth check
      }
    };
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const clearAuthDataAndPostHog = () => { // Wrap in useCallback if it were a dependency of useEffect that changes
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');
    setIsAuthenticated(false);
    setUser(null);
    setAccessToken(null);
    setRefreshTokenVal(null);

    if (posthog.__loaded) {
      posthog.reset(); // Clears user identity and generates a new anonymous ID
    }
  };

  const handleTokenRefresh = async (currentRefreshToken: string, isInitialLoad: boolean = false) => {
    setIsLoading(true);
    try {
      if (posthog.__loaded) {
        posthog.capture('token_refresh_initiated');
      }
      const response = await apiService.auth.refreshToken(currentRefreshToken);
      const { accessToken, idToken } = response.data;

      const newDecodedUser = decodeIdToken(idToken);
      if (newDecodedUser) {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('idToken', idToken);

        processUserLogin(newDecodedUser, accessToken, currentRefreshToken, idToken); // Reuse currentRefreshToken if not updated
        if (posthog.__loaded) {
          posthog.capture('token_refresh_successful');
        }
        return true;
      } else {
        throw new Error("Failed to decode new ID token.");
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
      if (posthog.__loaded) {
        posthog.capture('token_refresh_failed', { error: error.message });
      }
      if (!isInitialLoad) { // Don't show toast on initial silent refresh failure
        showErrorToast("Session Expired", "Please log in again.");
      }
      clearAuthDataAndPostHog();
      navigate('/admin/signin');
    } finally {
      setIsLoading(false);
    }
  };

  // New function to update user data
  const updateUserData = (newUserData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...newUserData };
      setUser(updatedUser);

      // PostHog: Update user properties
      if (posthog.__loaded && user.phoneNumber) {
        posthog.identify(user.phoneNumber, {
          name: `${updatedUser.firstName} ${updatedUser.lastName}`,
          phone: updatedUser.phoneNumber,
          profilePictureUrl: updatedUser.profilePicture,
        });
        posthog.capture('user_profile_updated_client_side', { updated_fields: Object.keys(newUserData) });
      }
    }
  };

  const login = (tokens: { accessToken: string; refreshToken: string; idToken: string }) => {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    localStorage.setItem('idToken', tokens.idToken);

    const decodedUser = decodeIdToken(tokens.idToken);
    if (decodedUser) {
      processUserLogin(decodedUser, tokens.accessToken, tokens.refreshToken, tokens.idToken);
      navigate('/admin'); // Redirect to admin dashboard after login
    } else {
      showErrorToast("Login Failed", "Could not parse user information.");
      clearAuthDataAndPostHog();
    }
  };

  const logout = () => {
    if (posthog.__loaded) {
      posthog.capture('logout_successful');
    }
    clearAuthDataAndPostHog();
    navigate('/admin/signin');
    showSuccessToast("Logged Out", "You have been successfully logged out.");
  };

  const initiateLoginOtp = async (phoneNumber: string, countryCode: string = "+91", showToasts: boolean = true) => {
    setIsLoading(true);
    if (posthog.__loaded) {
      posthog.capture('login_otp_initiation_attempted', { phoneNumber });
    }
    try {
      const response = await apiService.auth.initiateLogin({ phoneNumber, countryCode });
      // Only show toast if the flag is true
      if (showToasts) {
        showSuccessToast("OTP Sent", response.data.message || 'OTP sent successfully!');
      }
      if (posthog.__loaded) posthog.capture('login_otp_initiation_successful', { phoneNumber });
      return {
        otpIdentifier: response.data.otpIdentifier,
        userIdentifier: response.data.userIdentifier!,
      };
    } catch (error: any) {
      console.error("Initiate Login OTP error:", error);
      // Only show toast if the flag is true
      if (showToasts) {
        showErrorToast("OTP Error", error.response?.data?.message || "Failed to send OTP. Please try again.");
      }
      if (posthog.__loaded) posthog.capture('login_otp_initiation_failed', { phoneNumber, error: error.message });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const completeLoginOtp = async (phoneNumber: string, countryCode: string = "+91", otp: string, otpIdentifier: string, userIdentifier: string, showToasts: boolean = true) => {
    setIsLoading(true);
    if (posthog.__loaded) posthog.capture('login_otp_completion_attempted', { userIdentifier });
    try {
      const response = await apiService.auth.completeLogin({ phoneNumber, countryCode, otp, otpIdentifier, userIdentifier });
      login(response.data.data);
      // Only show toast if the flag is true
      if (showToasts) {
        showSuccessToast("Login Successful", response.data.message || "You are now logged in!");
      }
      return true;
    } catch (error: any) {
      console.error("Complete Login OTP error:", error);
      // Only show toast if the flag is true
      if (showToasts) {
        showErrorToast("Login Failed", error.response?.data?.message || "Login failed. Invalid OTP or an error occurred.");
      }
      if (posthog.__loaded) posthog.capture('login_otp_completion_failed', { userIdentifier, error: error.message });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    if (!refreshTokenVal) return false;

    setIsLoading(true);
    try {
      const response = await apiService.auth.refreshToken(refreshTokenVal);
      const { accessToken, idToken } = response.data;

      const newDecodedUser = decodeIdToken(idToken);
      if (newDecodedUser) {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('idToken', idToken);

        setAccessToken(accessToken);
        setUser({
          firstName: newDecodedUser['custom:firstName'] || '',
          lastName: newDecodedUser['custom:lastName'] || '',
          phoneNumber: newDecodedUser.phone_number || '',
          groups: newDecodedUser['cognito:groups'] || [],
          profilePicture: newDecodedUser['custom:profilePicUrl'] || '',
        });
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to refresh token:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      accessToken,
      refreshTokenVal,
      login,
      logout,
      initiateLoginOtp,
      completeLoginOtp,
      isLoading,
      updateUserData,
      refreshToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};